import type { NookListItem } from "@nook/api-client"
import { Badge, cn } from "@nook/ui"
import { nookHost, nookKind, nookTitle, savedLabel, SourceIcon } from "@nook/client-ui"
import { Link } from "@tanstack/react-router"
import { Loader2 } from "lucide-react"
import { useEffect, useRef, type ReactNode } from "react"

import { isNote } from "@/components/note-editor.js"

export const LIST_COLUMNS = "grid grid-cols-[minmax(0,1fr)_180px_120px_110px] items-center gap-4 px-6"

/** Keeps the keyboard's cursor on screen. */
export function useScrollIntoView<T extends HTMLElement>(selected: boolean) {
  const ref = useRef<T>(null)
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ block: "nearest" })
  }, [selected])
  return ref
}

/**
 * Opens its nook via `/?nook=`, a note full page (block 47), except while
 * optimistic (its id is the client's, and a detail route for it would be a
 * nook the server never heard of) or still processing.
 */
export function NookLink({ nook, selected, className, children }: { nook: NookListItem; selected: boolean; className: string; children: ReactNode }) {
  const ref = useScrollIntoView<HTMLAnchorElement>(selected)

  return nook.optimistic || nook.status === "processing" ? (
    <div className={cn(className, nook.optimistic && "opacity-60")}>{children}</div>
  ) : isNote(nook) ? (
    <Link ref={ref} to="/n/$id" params={{ id: nook.id }} className={className} data-selected={selected || undefined}>
      {children}
    </Link>
  ) : (
    <Link
      ref={ref}
      from="/"
      to="/"
      search={(prev) => ({ ...prev, nook: nook.id })}
      resetScroll={false}
      className={className}
      data-selected={selected || undefined}
    >
      {children}
    </Link>
  )
}

function Favicon({ nook }: { nook: NookListItem }) {
  return (
    <span className="bg-muted flex size-5 shrink-0 items-center justify-center rounded-[5px] border">
      {nook.status === "processing" ? (
        <Loader2 aria-hidden className="text-muted-foreground size-3 animate-spin" />
      ) : (
        <SourceIcon icon={nook.sourceIcon} className="size-3.5" />
      )}
    </span>
  )
}

/** One row of the list: title, source, type, saved. The cursor's row says what its keys do. */
export function NookRow({ nook, selected }: { nook: NookListItem; selected: boolean }) {
  return (
    <NookLink
      nook={nook}
      selected={selected}
      className={cn(LIST_COLUMNS, "hover:bg-accent/60 active:bg-accent h-12 border-b text-[0.8125rem]", selected && "bg-accent hover:bg-accent")}
    >
      <span className="flex min-w-0 items-center gap-3">
        <Favicon nook={nook} />
        <span className="truncate font-medium">{nookTitle(nook)}</span>
        {nook.status === "failed" ? (
          <Badge variant="outline" className="text-muted-foreground">
            Couldn&apos;t extract this page
          </Badge>
        ) : null}
      </span>
      <span className="text-muted-foreground truncate">{nookHost(nook) ?? "—"}</span>
      <span className="text-muted-foreground">{nookKind(nook)}</span>
      <span className="text-muted-foreground text-right text-xs tabular-nums">{savedLabel(isNote(nook) ? nook.editedAt : nook.capturedAt)}</span>
    </NookLink>
  )
}
